"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  FormControl,
  FormLabel,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
  useToast,
  HStack,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { VoicemailSettings } from "./VoicemailSettings";
import { agentService } from "../../../voice-services/agentService";
import type { VoicemailOption } from "../../../voice-services/agentService";

interface CallSettingsProps {
  agentId: string;
  initialSettings?: {
    enable_voicemail_detection?: boolean;
    end_call_after_silence_ms?: number;
    max_call_duration_ms?: number;
    begin_message_delay_ms?: number;
    voicemail_message?: string;
    voicemail_detection_timeout_ms?: number;
    voicemail_option?: VoicemailOption | null; // Updated type from string to VoicemailOption | null
  };
}

export const CallSettings: React.FC<CallSettingsProps> = ({
  agentId,
  initialSettings,
}) => {
  const [settings, setSettings] = useState(initialSettings || {});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();

  const panelBg = "gray.50";
  const cardBg = "white";
  const border = "gray.200";
  const label = "gray.700";
  const subtl = "gray.600";

  useEffect(() => {
    const fetchAgentSettings = async () => {
      setIsLoading(true);
      try {
        const data = await agentService.getAgentDetails(agentId);
        console.log("[v0] CallSettings - Fetched agent settings:", data); // Added log for debugging
        setSettings({
          enable_voicemail_detection: data.enable_voicemail_detection,
          end_call_after_silence_ms: data.end_call_after_silence_ms,
          max_call_duration_ms: data.max_call_duration_ms,
          begin_message_delay_ms: data.begin_message_delay_ms,
          voicemail_message: data.voicemail_message,
          voicemail_detection_timeout_ms: data.voicemail_detection_timeout_ms,
          voicemail_option: data.voicemail_option,
        });
      } catch (error) {
        console.error("Error fetching agent settings:", error);
        toast({
          title: "Error fetching agent settings",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentSettings();
  }, [agentId, toast]);

  const formatTime = (ms: number) => {
    if (!ms && ms !== 0) return "—";
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)} m`;
    return `${(ms / 3_600_000).toFixed(2)} h`;
  };

  const updateAgentSettings = useCallback(
    async (newSettings: Partial<typeof settings>) => {
      setIsUpdating(true);
      try {
        await agentService.updateAgentSettings(agentId, newSettings);
        setSettings((prev) => ({ ...prev, ...newSettings }));
        toast({
          title: "Call settings updated",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        console.error("Error updating call settings:", error);
        toast({
          title: "Error updating call settings",
          description:
            error?.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, toast],
  );

  const handleEndCallAfterSilenceChange = useCallback(
    async (value: number) => {
      await updateAgentSettings({ end_call_after_silence_ms: value });
    },
    [updateAgentSettings],
  );

  const handleMaxCallDurationChange = useCallback(
    async (value: number) => {
      await updateAgentSettings({ max_call_duration_ms: value });
    },
    [updateAgentSettings],
  );

  const handleBeginMessageDelayChange = useCallback(
    async (value: number) => {
      await updateAgentSettings({ begin_message_delay_ms: value });
    },
    [updateAgentSettings],
  );

  if (isLoading) {
    return (
      <Flex h="100%" w="100%" justify="center" align="center">
        <Spinner size="md" color="primary.500" />
      </Flex>
    );
  }

  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={3}
            px={4}
            borderRadius="lg"
            bg="transparent"
            _hover={{ bg: "primary.50" }}
            _active={{ bg: "primary.100" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
              color="primary.600"
            >
              {/* small accent icon */}
              <Box as="span" lineHeight={0}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10 4C8.4087 4 6.8826 4.6321 5.7574 5.7574C4.6321 6.8826 4 8.4087 4 10H6.25C6.6478 10 7.0294 10.158 7.3107 10.4393C7.592 10.7206 7.75 11.1022 7.75 11.5V15.25C7.75 15.6478 7.592 16.0294 7.3107 16.3107C7.0294 16.592 6.6478 16.75 6.25 16.75H4C3.6022 16.75 3.2206 16.592 2.9393 16.3107C2.658 16.0294 2.5 15.6478 2.5 15.25V10C2.5 5.8578 5.8578 2.5 10 2.5C14.1423 2.5 17.5 5.8578 17.5 10V15.25C17.5 15.6478 17.342 16.0294 17.0607 16.3107C16.7794 16.592 16.3978 16.75 16 16.75H13.75C13.3522 16.75 12.9706 16.592 12.6893 16.3107C12.408 16.0294 12.25 15.6478 12.25 15.25V11.5C12.25 11.1022 12.408 10.7206 12.6893 10.4393C12.9706 10.158 13.3522 10 13.75 10H16C16 8.4087 15.3679 6.8826 14.2426 5.7574C13.1174 4.6321 11.5913 4 10 4ZM4 11.5V15.25H6.25V11.5H4ZM13.75 11.5V15.25H16V11.5H13.75Z" />
                </svg>
              </Box>
              Call Settings
            </Box>
            <AccordionIcon color="primary.600" />
          </AccordionButton>
        </h2>

        <AccordionPanel pb={4} bg="primary.50" borderRadius="lg" mt={3} px={4}>
          <VStack spacing={6} align="stretch">
            {/* Voicemail settings block */}
            <Box
              bg={cardBg}
              borderRadius="lg"
              p={4}
              border="1px"
              borderColor={border}
            >
              <VoicemailSettings
                agentId={agentId}
                initialSettings={{
                  voicemail_option: settings.voicemail_option, // Added voicemail_option to pass the new API format
                  enable_voicemail_detection:
                    settings.enable_voicemail_detection,
                  voicemail_message: settings.voicemail_message,
                  voicemail_detection_timeout_ms:
                    settings.voicemail_detection_timeout_ms,
                }}
              />
            </Box>

            {/* End Call on Silence */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium" color={label} mb={1}>
                End Call on Silence
              </FormLabel>
              <HStack justify="space-between" mb={2} align="center">
                <Slider
                  aria-label="End call after silence"
                  min={10_000}
                  max={1_800_000}
                  step={10_000}
                  value={settings.end_call_after_silence_ms || 0}
                  onChange={(value) =>
                    setSettings((prev: any) => ({
                      ...prev,
                      end_call_after_silence_ms: value,
                    }))
                  }
                  onChangeEnd={handleEndCallAfterSilenceChange}
                  isDisabled={isUpdating}
                  width="80%"
                  colorScheme="brand"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={4} />
                </Slider>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={label}
                  minW="64px"
                  textAlign="right"
                >
                  {formatTime(settings.end_call_after_silence_ms || 0)}
                </Text>
              </HStack>
              <Text fontSize="xs" color={subtl}>
                Auto-hangup after prolonged silence from the caller.
              </Text>
            </FormControl>

            {/* Max Call Duration */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium" color={label} mb={1}>
                Max Call Duration
              </FormLabel>
              <HStack justify="space-between" mb={2} align="center">
                <Slider
                  aria-label="Max call duration"
                  min={60_000}
                  max={7_200_000}
                  step={60_000}
                  value={settings.max_call_duration_ms || 0}
                  onChange={(value) =>
                    setSettings((prev: any) => ({
                      ...prev,
                      max_call_duration_ms: value,
                    }))
                  }
                  onChangeEnd={handleMaxCallDurationChange}
                  isDisabled={isUpdating}
                  width="80%"
                  colorScheme="brand"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={4} />
                </Slider>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={label}
                  minW="64px"
                  textAlign="right"
                >
                  {formatTime(settings.max_call_duration_ms || 0)}
                </Text>
              </HStack>
              <Text fontSize="xs" color={subtl}>
                Hard cap to prevent excessive talk time.
              </Text>
            </FormControl>

            {/* Pause Before Speaking */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium" color={label} mb={1}>
                Pause Before Speaking
              </FormLabel>
              <Text fontSize="xs" color={subtl} mb={2}>
                How long the assistant waits before starting the first line.
              </Text>
              <HStack justify="space-between" mb={2} align="center">
                <Slider
                  aria-label="Begin message delay"
                  min={0}
                  max={5_000}
                  step={100}
                  value={settings.begin_message_delay_ms || 0}
                  onChange={(value) =>
                    setSettings((prev: any) => ({
                      ...prev,
                      begin_message_delay_ms: value,
                    }))
                  }
                  onChangeEnd={handleBeginMessageDelayChange}
                  isDisabled={isUpdating}
                  width="80%"
                  colorScheme="brand"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={4} />
                </Slider>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={label}
                  minW="64px"
                  textAlign="right"
                >
                  {formatTime(settings.begin_message_delay_ms || 0)}
                </Text>
              </HStack>
            </FormControl>
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
